import {
    showLoader,
    hideLoader,
    axios,
    getAppState,
    getAppData,
    getAppConfig,
    environment,
} from 'larasnap';
import { showError, moment } from 'larasnap/support';
import { listenOnce, socketId } from 'larasnap/echo';
import { loadAppState } from '../core/AppState';
import { uploadFilesToS3 } from 'larasnap/vapor';

const isDateValue = value =>
    value instanceof Date ||
    (moment.isMoment(value) && value.isValid()) ||
    moment(value, getAppConfig('date_format'), true).isValid();

const buildUrl = (path, ...params) => {
    params = params.map(param => {
        if (typeof param === 'boolean') {
            return param ? '1' : '0';
        } else if (isDateValue(param)) {
            return moment(param).format('YYYY-MM-DD');
        } else {
            return param;
        }
    });

    if (!path.endsWith('/')) path += '/';
    return path + params.join('/');
};

const createInstance = config => {
    const instance = axios.create(config);

    let url,
        method,
        params = {},
        data = {},
        loaderIsVisible = true,
        hideErrors = false;

    const loadingState = [];
    const hiddenErrorCodes = [422];

    const onBeforeSend = config => {
        /* if (environment('local')) {
            let url = config.url;
            if (config.baseURL) url = config.baseURL + (url ?? '');
            console.log(config.method.toUpperCase() + ': ' + url);
        } */

        const data = JSON.stringify({
            state: getAppState(),
        }).replace(/[^ -~]+/g, ''); // Non ASCII chars with cause an error

        config.headers['x-larasnap-request'] = data;
        config.headers['X-Socket-ID'] = socketId;

        applyLoadingState();

        config.params = {
            ...config.params,
            ...params,
        };

        return config;
    };

    const onComplete = () => {
        removeLoadingState();
    };

    const onSuccess = response => {
        const { redirect, state, content } = response.data;

        // Check if a redirect command was returned by the
        // server hander and if so redirect accordingly
        if (redirect) {
            window.location.href = redirect;
            return new Promise(() => {});
        }

        loadAppState(state);

        if (response.headers['x-larasnap-server-promise']) {
            return handleServerPromise(response);
        } else {
            onComplete();
            response.data = content;
            return response;
        }
    };

    const handleServerPromise = response => {
        const id = response.headers['x-larasnap-server-promise'];
        return new Promise((resolve, reject) =>
            listenOnce(`.${id}`, promise => {
                onComplete();

                response.data = promise.value;
                response.is_server_promise = true;

                if (promise.state === 'resolved') {
                    resolve(response);
                } else if (promise.state === 'rejected') {
                    response.status = 500;
                    onError({ response });
                    reject(promise.value);
                }
            }),
        );
    };

    const onError = error => {
        onComplete();
        console.error('error', error);
        const response = error.response;

        if (!response) {
            showError('Unknown Error');
        } else if (!hideErrors && !hiddenErrorCodes.includes(response.status)) {
            if (response.data) {
                showError(response.data, response.status);
            } else if (response.statusText) {
                showError(
                    response.statusText + ' ' + response.status,
                    response.status,
                );
            } else {
                showError('Unknown Server Error', response.status);
            }
        }

        return Promise.reject(error);
    };

    const withS3FileUploads = parentMethod => (url, data, config) => {
        applyLoadingState();

        return uploadFilesToS3(data)
            .catch(error => {
                removeLoadingState();

                if (!hideErrors) {
                    showError('Could not upload file');
                    builder.route('report_error').post('', {
                        type: 'Vapor File Upload Error',
                        data: {
                            message: error.message,
                        },
                    });
                }
                return Promise.reject(error);
            })
            .then(data => {
                removeLoadingState();
                return parentMethod(url, data, config);
            });
    };

    instance.url = (path, ...params) => {
        url = buildUrl(path, ...params);
        return instance;
    };

    instance.method = value => {
        method = value;
        return instance;
    };

    instance.addParams = value => {
        Object.assign(params, value);
        return instance;
    };

    instance.addData = value => {
        Object.assign(data, value);
        return instance;
    };

    instance.setData = value => {
        data = value;
        return instance;
    };

    instance.send = (config = {}) => {
        const _method = method.toLowerCase();
        if (['post', 'put', 'patch'].includes(_method)) {
            return instance[_method](url, data, config);
        } else {
            return instance[_method](url, config);
        }
    };

    instance.withLoader = isVisible => {
        loaderIsVisible = isVisible;
        return instance;
    };

    instance.withButton = button => {
        button.attachHttpBuilder(instance);
        return instance;
    };

    instance.hideErrors = (isHidden = true) => {
        hideErrors = isHidden;
        return instance;
    };

    instance.hideErrorCode = errorCodes => {
        errorCodes = Array.isArray(errorCodes) ? errorCodes : [errorCodes];
        hiddenErrorCodes.push(...errorCodes);
        return instance;
    };

    instance.addRequestInterceptor = (...args) => {
        instance.interceptors.request.use(...args);
        return instance;
    };

    instance.addResponseInterceptor = (...args) => {
        instance.interceptors.response.use(...args);
        return instance;
    };

    const applyLoadingState = () => {
        loadingState.forEach(x => x.apply());
    };

    const removeLoadingState = () => {
        loadingState.forEach(x => x.remove());
    };

    instance.withLoadingState = obj => {
        loadingState.push(obj);
        return instance;
    };

    instance.post = withS3FileUploads(instance.post);
    instance.put = withS3FileUploads(instance.put);
    instance.patch = withS3FileUploads(instance.patch);

    instance.withLoadingState({
        apply: () => (loaderIsVisible ? showLoader() : undefined),
        remove: () => (loaderIsVisible ? hideLoader() : undefined),
    });

    instance.addRequestInterceptor(onBeforeSend, null, { synchronous: true });
    instance.addResponseInterceptor(onSuccess, onError);

    return instance;
};

const createInstanceForRoute = (name, ...params) =>
    createInstance({
        baseURL: buildUrl(getAppData('routes')[name], ...params),
    });

const get = (target, name) => {
    if (name === 'route') {
        return createInstanceForRoute;
    } else if (name === 'create') {
        return createInstance;
    } else {
        return createInstance()[name];
    }
};

const builder = new Proxy({}, { get });

export default builder;

//export default createInstance;

/*
API Changes

Rename hideErrorCodes to hideErrorCode
Rename setMethod = method
Refactor for return type 'response' instead of 'data'
*/
